import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { EventsClient } from '../events-client.js';
import Logger from '../utils/logger.js';
import {
    createRoomsMenuSchema,
    readRoomsSchemaButtonLabel,
    ROOMS_MENU_ACTIONS,
    ROOMS_MENU_LABELS,
} from './schemas/rooms-menu-schema.js';

const MODULE = 'RoomsSubmenu';

const CSS = `
        .mm-rooms-section {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            margin-top: 0.5rem;
        }

        .mm-rooms-table {
            width: 100%;
            border-collapse: collapse;
            font-size: clamp(0.78rem, 2.1vw, 0.92rem);
        }

        .mm-rooms-table th {
            color: #b8e3f0;
            font-weight: 600;
            text-align: left;
            padding: 0.35rem 0.5rem;
            border-bottom: 1px solid rgba(0, 217, 255, 0.35);
        }

        .mm-rooms-table td {
            padding: 0.35rem 0.5rem;
            color: #e0f4ff;
            border-bottom: 1px solid rgba(0, 217, 255, 0.12);
            cursor: pointer;
        }

        .mm-rooms-table tr.mm-rooms-row:hover td {
            background: rgba(0, 217, 255, 0.08);
        }

        .mm-rooms-table tr.mm-rooms-row.selected td {
            background: rgba(0, 217, 255, 0.18);
            color: #00ffff;
        }

        .mm-rooms-private-yes {
            color: #ff9d00;
            font-weight: 700;
        }

        .mm-rooms-private-no {
            color: #00ff9d;
            font-weight: 700;
        }

        .mm-rooms-current-badge {
            font-size: 0.75rem;
            color: #00d9ff;
            margin-left: 0.4rem;
            opacity: 0.8;
        }

        .mm-rooms-password-row {
            display: flex;
            flex-direction: column;
            gap: 0.3rem;
        }

        .mm-rooms-password-row label {
            color: #b8e3f0;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
        }

        .mm-rooms-status {
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
            min-height: 1.2em;
        }

        .mm-rooms-status.error {
            color: #ff4444;
            text-shadow: 0 0 6px rgba(255, 68, 68, 0.5);
        }

        .mm-rooms-status.success {
            color: #00ff9d;
            text-shadow: 0 0 6px rgba(0, 255, 157, 0.5);
        }

        .mm-rooms-status.info {
            color: #00d9ff;
        }

        .mm-rooms-empty {
            color: rgba(184, 227, 240, 0.5);
            font-style: italic;
            text-align: center;
            padding: 1rem 0;
        }

        .mm-rooms-divider {
            border: none;
            border-top: 1px solid rgba(0, 217, 255, 0.25);
            margin: 0.5rem 0;
        }

        .mm-rooms-create-section {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
        }

        .mm-rooms-create-section h3 {
            color: #b8e3f0;
            font-size: clamp(0.82rem, 2.2vw, 0.96rem);
            font-weight: 700;
            margin: 0 0 0.25rem 0;
            text-transform: uppercase;
            letter-spacing: 0.06em;
        }

        .mm-rooms-create-row {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }

        .mm-rooms-create-row label {
            color: #b8e3f0;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
        }
`;

const MM_ROOMS_HTML = `
        <div id="menu-modal-rooms" class="menu-modal-content">
            <h2>Rooms</h2>
            <div class="mm-rooms-section">
                <div>
                    <table class="mm-rooms-table">
                        <thead>
                            <tr>
                                <th>Room Name</th>
                                <th>Users</th>
                                <th>Access</th>
                            </tr>
                        </thead>
                        <tbody id="mm-rooms-tbody">
                            <tr><td colspan="3" class="mm-rooms-empty">Loading rooms…</td></tr>
                        </tbody>
                    </table>
                </div>
                <form onsubmit="return false;" autocomplete="off">
                <div id="mm-rooms-password-row" class="mm-rooms-password-row" style="display:none;">
                    <label for="mm-rooms-password-input">Room Password</label>
                    <input id="mm-rooms-password-input" class="mm-text-input" type="password"
                           autocomplete="off" placeholder="Enter room password"
                           onkeydown="event.stopPropagation()" />
                </div>
                <div>
                    <button id="mm-rooms-join-button" class="mm-submit-button" disabled>Join Room</button>
                    <button id="mm-rooms-refresh-button" class="mm-submit-button" style="margin-left:0.5rem;">Refresh</button>
                </div>
                </form>
                <div id="mm-rooms-status" class="mm-rooms-status"></div>

                <hr class="mm-rooms-divider" />

                <div class="mm-rooms-create-section">
                    <h3>Create New Room</h3>
                    <form onsubmit="return false;" autocomplete="off">
                    <div class="mm-rooms-create-row">
                        <label for="mm-rooms-create-name">Room Name</label>
                        <input id="mm-rooms-create-name" class="mm-text-input" type="text"
                               autocomplete="off" placeholder="Enter room name"
                               onkeydown="event.stopPropagation()" />
                    </div>
                    <div class="mm-rooms-create-row">
                        <label for="mm-rooms-create-password">Room Password (optional)</label>
                        <input id="mm-rooms-create-password" class="mm-text-input" type="password"
                               autocomplete="new-password" placeholder="Leave blank for public room"
                               onkeydown="event.stopPropagation()" />
                    </div>
                    <div>
                        <button id="mm-rooms-create-button" class="mm-submit-button">Create / Join Room</button>
                    </div>
                    </form>
                    <div id="mm-rooms-create-status" class="mm-rooms-status"></div>
                </div>
            </div>
        </div>
`;

interface RoomEntry {
    name: string;
    isPrivate: boolean;
    userCount: number;
}

export class RoomsSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'RoomsSubmenuDH';

    private static readonly JOIN_LABEL_READY = 'JOIN ROOM';
    private static readonly JOIN_LABEL_SELECT = 'SELECT A ROOM TO JOIN';

    private styleEl: HTMLStyleElement | null = null;
    private selectedRoom: RoomEntry | null = null;

    updateConditionals(): void {
        // No periodic refresh needed.
    }

    displayMenuContent(containerDiv: HTMLDivElement): void {
        this.styleEl = document.createElement('style');
        this.styleEl.innerText = CSS;
        document.head.appendChild(this.styleEl);

        containerDiv.innerHTML = MM_ROOMS_HTML;
        const roomsSchema = createRoomsMenuSchema();
        this.applySchemaLabels(containerDiv, roomsSchema);

        const eventsClient = EventsClient.instance;
        if (!eventsClient) {
            Logger.error(MODULE, 'EventsClient singleton not available.');
            return;
        }

        const token = eventsClient.getAuthToken();
        if (!token) {
            this.setStatus(ROOMS_MENU_LABELS.notAuthenticated, 'error');
            return;
        }

        const joinBtn = document.getElementById('mm-rooms-join-button') as HTMLButtonElement | null;
        const refreshBtn = document.getElementById('mm-rooms-refresh-button') as HTMLButtonElement | null;
        const createBtn = document.getElementById('mm-rooms-create-button') as HTMLButtonElement | null;

        this.setJoinButtonState(false);

        if (joinBtn) {
            joinBtn.addEventListener('click', () => {
                this.joinSelectedRoom(eventsClient);
            });
        }

        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
                this.loadRooms(eventsClient, token);
            });
        }

        if (createBtn) {
            createBtn.addEventListener('click', () => {
                this.createRoom(eventsClient);
            });
        }

        this.loadRooms(eventsClient, token);
    }

    hideContent(): void {
        if (this.styleEl && this.styleEl.parentNode) {
            this.styleEl.parentNode.removeChild(this.styleEl);
            this.styleEl = null;
        }
        this.selectedRoom = null;
    }

    private applySchemaLabels(containerDiv: HTMLDivElement, roomsSchema: ReturnType<typeof createRoomsMenuSchema>): void {
        const titleEl = containerDiv.querySelector('h2');
        if (titleEl) {
            titleEl.textContent = roomsSchema.title;
        }

        const headers = containerDiv.querySelectorAll('.mm-rooms-table th');
        if (headers[0]) headers[0].textContent = 'Room Name';
        if (headers[1]) headers[1].textContent = 'Users';
        if (headers[2]) headers[2].textContent = 'Access';

        const passwordLabel = containerDiv.querySelector('label[for="mm-rooms-password-input"]');
        if (passwordLabel) {
            passwordLabel.textContent = ROOMS_MENU_LABELS.roomPassword;
        }

        const passwordInput = document.getElementById('mm-rooms-password-input') as HTMLInputElement | null;
        if (passwordInput) {
            passwordInput.placeholder = ROOMS_MENU_LABELS.passwordPlaceholder;
        }

        const joinBtn = document.getElementById('mm-rooms-join-button') as HTMLButtonElement | null;
        if (joinBtn) {
            joinBtn.textContent = RoomsSubmenuDH.JOIN_LABEL_SELECT;
        }

        const refreshBtn = document.getElementById('mm-rooms-refresh-button') as HTMLButtonElement | null;
        if (refreshBtn) {
            refreshBtn.textContent = readRoomsSchemaButtonLabel(roomsSchema, ROOMS_MENU_ACTIONS.refreshRooms, ROOMS_MENU_LABELS.refresh);
        }

        const createHeader = containerDiv.querySelector('.mm-rooms-create-section h3');
        if (createHeader) {
            createHeader.textContent = ROOMS_MENU_LABELS.createNewRoom;
        }

        const createNameLabel = containerDiv.querySelector('label[for="mm-rooms-create-name"]');
        if (createNameLabel) {
            createNameLabel.textContent = ROOMS_MENU_LABELS.roomName;
        }

        const createNameInput = document.getElementById('mm-rooms-create-name') as HTMLInputElement | null;
        if (createNameInput) {
            createNameInput.placeholder = ROOMS_MENU_LABELS.roomNamePlaceholder;
        }

        const createPasswordLabel = containerDiv.querySelector('label[for="mm-rooms-create-password"]');
        if (createPasswordLabel) {
            createPasswordLabel.textContent = ROOMS_MENU_LABELS.createPasswordLabel;
        }

        const createPasswordInput = document.getElementById('mm-rooms-create-password') as HTMLInputElement | null;
        if (createPasswordInput) {
            createPasswordInput.placeholder = ROOMS_MENU_LABELS.createPasswordPlaceholder;
        }

        const createBtn = document.getElementById('mm-rooms-create-button') as HTMLButtonElement | null;
        if (createBtn) {
            createBtn.textContent = readRoomsSchemaButtonLabel(roomsSchema, ROOMS_MENU_ACTIONS.createRoom, ROOMS_MENU_LABELS.createJoinRoom);
        }
    }

    private async loadRooms(eventsClient: EventsClient, token: string): Promise<void> {
        const tbody = document.getElementById('mm-rooms-tbody');
        if (tbody) tbody.innerHTML = `<tr><td colspan="3" class="mm-rooms-empty">${ROOMS_MENU_LABELS.loadingRooms}</td></tr>`;

        this.selectedRoom = null;
        this.setJoinButtonState(false);
        this.setPasswordRowVisible(false);
        this.setStatus('', '');

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/rooms`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!resp.ok) {
                Logger.warn(MODULE, `Failed to load rooms: ${resp.status}`);
                this.setStatus(ROOMS_MENU_LABELS.failedToLoadRooms, 'error');
                if (tbody) tbody.innerHTML = `<tr><td colspan="3" class="mm-rooms-empty">${ROOMS_MENU_LABELS.failedToLoadRooms}</td></tr>`;
                return;
            }

            const rooms = (await resp.json()) as RoomEntry[];

            if (!tbody) return;

            if (rooms.length === 0) {
                tbody.innerHTML = `<tr><td colspan="3" class="mm-rooms-empty">${ROOMS_MENU_LABELS.noActiveRooms}</td></tr>`;
                return;
            }

            const currentRoom = eventsClient.getCurrentRoom();
            tbody.innerHTML = '';

            for (const room of rooms) {
                const tr = document.createElement('tr');
                tr.classList.add('mm-rooms-row');
                tr.dataset['room'] = room.name;
                tr.dataset['private'] = String(room.isPrivate);

                const nameTd = document.createElement('td');
                nameTd.textContent = room.name;
                if (room.name === currentRoom) {
                    const badge = document.createElement('span');
                    badge.className = 'mm-rooms-current-badge';
                    badge.textContent = '(current)';
                    nameTd.appendChild(badge);
                }

                const userCountTd = document.createElement('td');
                userCountTd.textContent = String(room.userCount ?? 0);

                const privateTd = document.createElement('td');
                privateTd.innerHTML = room.isPrivate
                    ? '<span class="mm-rooms-private-yes">private</span>'
                    : '<span class="mm-rooms-private-no">public</span>';

                tr.appendChild(nameTd);
                tr.appendChild(userCountTd);
                tr.appendChild(privateTd);

                tr.addEventListener('click', () => this.onRoomRowClick(room, tr));
                tbody.appendChild(tr);
            }
        } catch (err) {
            Logger.error(MODULE, 'Error loading rooms', err);
            this.setStatus(ROOMS_MENU_LABELS.errorLoadingRooms, 'error');
            if (tbody) tbody.innerHTML = `<tr><td colspan="3" class="mm-rooms-empty">${ROOMS_MENU_LABELS.errorLoadingRooms}</td></tr>`;
        }
    }

    private onRoomRowClick(room: RoomEntry, tr: HTMLTableRowElement): void {
        // Deselect any previously selected row
        const tbody = document.getElementById('mm-rooms-tbody');
        if (tbody) {
            for (const existingRow of Array.from(tbody.querySelectorAll('.mm-rooms-row.selected'))) {
                existingRow.classList.remove('selected');
            }
        }

        tr.classList.add('selected');
        this.selectedRoom = room;
        this.setStatus('', '');
        this.setJoinButtonState(true);

        this.setPasswordRowVisible(room.isPrivate);

        const pwInput = document.getElementById('mm-rooms-password-input') as HTMLInputElement | null;
        if (pwInput) pwInput.value = '';
    }

    private async joinSelectedRoom(eventsClient: EventsClient): Promise<void> {
        if (!this.selectedRoom) return;

        const currentRoom = eventsClient.getCurrentRoom();
        if (this.selectedRoom.name === currentRoom) {
            this.setStatus('You are already in this room.', 'info');
            return;
        }

        const pwInput = document.getElementById('mm-rooms-password-input') as HTMLInputElement | null;
        const roomPassword = pwInput ? pwInput.value : '';

        const joinBtn = document.getElementById('mm-rooms-join-button') as HTMLButtonElement | null;
        const refreshBtn = document.getElementById('mm-rooms-refresh-button') as HTMLButtonElement | null;

        if (joinBtn) joinBtn.disabled = true;
        if (refreshBtn) refreshBtn.disabled = true;
        this.setStatus('Joining room…', 'info');

        try {
            await eventsClient.switchRoom(this.selectedRoom.name, roomPassword);
            this.setStatus('Joined room successfully!', 'success');
            const token = eventsClient.getAuthToken();
            if (token) this.loadRooms(eventsClient, token);
        } catch (e: unknown) {
            const msg = (e instanceof Error) ? e.message : String(e);
            if (msg === 'ROOM_PASSWORD_INCORRECT') {
                this.setStatus('Room password incorrect.', 'error');
            } else if (msg === 'ROOM_FULL') {
                this.setStatus('This room is full.', 'error');
            } else if (msg === 'ROOM_LIMIT_REACHED') {
                this.setStatus('The server has reached its room limit.', 'error');
            } else if (msg === 'ROOM_CREATE_COOLDOWN') {
                this.setStatus('You recently created a room. Please wait before creating another.', 'error');
            } else {
                Logger.error(MODULE, 'Error joining room', e);
                this.setStatus('Failed to join room.', 'error');
            }
            if (joinBtn) joinBtn.disabled = false;
            if (refreshBtn) refreshBtn.disabled = false;
        }
    }

    private setPasswordRowVisible(visible: boolean): void {
        const row = document.getElementById('mm-rooms-password-row');
        if (row) row.style.display = visible ? 'flex' : 'none';
    }

    private setJoinButtonState(roomSelected: boolean): void {
        const joinBtn = document.getElementById('mm-rooms-join-button') as HTMLButtonElement | null;
        if (!joinBtn) return;
        joinBtn.disabled = !roomSelected;
        joinBtn.textContent = roomSelected
            ? RoomsSubmenuDH.JOIN_LABEL_READY
            : RoomsSubmenuDH.JOIN_LABEL_SELECT;
    }

    private setStatus(message: string, type: '' | 'success' | 'error' | 'info'): void {
        const el = document.getElementById('mm-rooms-status');
        if (!el) return;
        el.textContent = message;
        el.className = `mm-rooms-status${type ? ` ${type}` : ''}`;
    }

    private setCreateStatus(message: string, type: '' | 'success' | 'error' | 'info'): void {
        const el = document.getElementById('mm-rooms-create-status');
        if (!el) return;
        el.textContent = message;
        el.className = `mm-rooms-status${type ? ` ${type}` : ''}`;
    }

    private async createRoom(eventsClient: EventsClient): Promise<void> {
        const nameInput = document.getElementById('mm-rooms-create-name') as HTMLInputElement | null;
        const pwInput = document.getElementById('mm-rooms-create-password') as HTMLInputElement | null;
        const createBtn = document.getElementById('mm-rooms-create-button') as HTMLButtonElement | null;

        const roomName = nameInput ? nameInput.value.trim() : '';
        if (!roomName) {
            this.setCreateStatus('Please enter a room name.', 'error');
            return;
        }

        const roomPassword = pwInput ? pwInput.value : '';

        if (createBtn) createBtn.disabled = true;
        this.setCreateStatus('Creating room…', 'info');

        try {
            await eventsClient.switchRoom(roomName, roomPassword);
            this.setCreateStatus('Joined room successfully!', 'success');
            if (nameInput) nameInput.value = '';
            if (pwInput) pwInput.value = '';
            const token = eventsClient.getAuthToken();
            if (token) this.loadRooms(eventsClient, token);
        } catch (e: unknown) {
            const msg = (e instanceof Error) ? e.message : String(e);
            if (msg === 'ROOM_PASSWORD_INCORRECT') {
                this.setCreateStatus('Room already exists and the password is incorrect.', 'error');
            } else if (msg === 'ROOM_FULL') {
                this.setCreateStatus('This room is full.', 'error');
            } else if (msg === 'ROOM_LIMIT_REACHED') {
                this.setCreateStatus('The server has reached its room limit.', 'error');
            } else if (msg === 'ROOM_CREATE_COOLDOWN') {
                this.setCreateStatus('You recently created a room. Please wait before creating another.', 'error');
            } else {
                Logger.error(MODULE, 'Error creating/joining room', e);
                this.setCreateStatus('Failed to create or join room.', 'error');
            }
        } finally {
            if (createBtn) createBtn.disabled = false;
        }
    }
}
